import React, { useState } from 'react';
import { 
  Calendar, Layout, Target, Brush, 
  Search, Bell, User, ChevronDown, Store,
  BarChart, Package, AlertCircle, MapPin, TrendingUp
} from 'lucide-react';
import { colors, agentColors } from '../../constants/theme';
import { AgentId, Agent } from '../../types/agent.types';

const agents: Agent[] = [
  { id: 'demand', name: '需要予測エージェント', icon: 'TrendingUp', color: '#06B6D4' },
  { id: 'md', name: '52週MDエージェント', icon: 'Calendar', color: '#4D8FEA' },
  { id: 'inventory', name: '在庫最適化エージェント', icon: 'Package', color: '#5CC8A1' },
  { id: 'planogram', name: '棚割り最適化エージェント', icon: 'Layout', color: '#8B5CF6' },
  { id: 'creative', name: 'チラシ生成エージェント', icon: 'Brush', color: '#F5A623' },
  { id: 'marketing', name: '企画支援エージェント', icon: 'Target', color: '#E85C56' },
  { id: 'alerts', name: 'アラート', icon: 'AlertCircle', color: '#EF4444' }
];

// 具体的な店舗リスト
const stores = [
  { id: 'shibuya', name: '渋谷店', area: '都心', sales: '特A' },
  { id: 'shinjuku', name: '新宿店', area: '都心', sales: '特A' },
  { id: 'ginza', name: '銀座店', area: '都心', sales: 'A' },
  { id: 'ikebukuro', name: '池袋店', area: '都心', sales: 'A' },
  { id: 'yokohama', name: '横浜店', area: '郊外', sales: 'A' },
  { id: 'chiba', name: '千葉店', area: '郊外', sales: 'B' },
  { id: 'omiya', name: '大宮店', area: '郊外', sales: 'B' },
  { id: 'tachikawa', name: '立川店', area: '郊外', sales: 'C' },
  { id: 'funabashi', name: '船橋店', area: '郊外', sales: 'C' },
];

const iconMap = {
  Calendar,
  Layout,
  Target,
  Brush,
  BarChart,
  Package,
  AlertCircle,
  TrendingUp
} as const;

interface HeaderProps {
  activeAgent: AgentId;
  setActiveAgent: (agent: AgentId) => void;
  environment?: any; // 後方互換性のため残す
  setEnvironment?: (env: any) => void;
}

const Header: React.FC<HeaderProps> = ({
  activeAgent,
  setActiveAgent,
}) => {
  const [selectedStore, setSelectedStore] = useState('shibuya');
  return (
    <header className="flex items-center justify-between px-6 py-3 border-b" 
      style={{ borderColor: colors.ui.border }}>
      <div className="flex items-center gap-6">
        {/* ロゴ */}
        <div className="flex items-center gap-3">
          <img 
            src="/logo.svg" 
            alt="Retail Agent" 
            className="h-8 w-auto"
            onError={(e) => {
              // ロゴが見つからない場合はテキストを表示
              e.currentTarget.style.display = 'none';
              const textLogo = document.getElementById('text-logo');
              if (textLogo) textLogo.style.display = 'flex';
            }}
          />
          <div 
            id="text-logo" 
            className="hidden items-center gap-2"
          >
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: colors.accent.blue }}
            >
              RA
            </div>
            <span className="font-semibold text-lg" style={{ color: colors.text.primary }}>
              Retail Agent
            </span>
          </div>
        </div>
        
        {/* エージェント切替 */}
        <div className="flex items-center gap-2 border-l pl-6" style={{ borderColor: colors.ui.border }}>
          {agents.map(agent => {
            const Icon = iconMap[agent.icon as keyof typeof iconMap];
            return (
              <button
                key={agent.id}
                onClick={() => setActiveAgent(agent.id)}
                className={`p-2 rounded-lg transition-all ${
                  activeAgent === agent.id ? 'shadow-lg' : 'hover:bg-gray-50'
                }`}
                style={{
                  backgroundColor: activeAgent === agent.id ? agent.color : 'transparent',
                  color: activeAgent === agent.id ? 'white' : colors.text.secondary
                }}
                title={agent.name}
              >
                <Icon size={20} />
              </button>
            );
          })}
        </div>

      </div>

      <div className="flex items-center gap-4">
        {/* グローバル検索 */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border" 
          style={{ borderColor: colors.ui.border }}>
          <Search size={16} style={{ color: colors.text.secondary }} />
          <input
            type="text"
            placeholder="SKU・カテゴリ・企画名を検索"
            className="w-64 text-sm outline-none bg-transparent"
            style={{ color: colors.text.primary }}
          />
        </div>

        {/* 通知 */}
        <button className="relative p-2 rounded-lg hover:bg-gray-50">
          <Bell size={20} style={{ color: colors.text.primary }} />
          <span 
            className="absolute top-1 right-1 w-2 h-2 rounded-full" 
            style={{ backgroundColor: colors.accent.red }} 
          />
        </button>

        {/* ユーザー */}
        <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
          <User size={20} style={{ color: colors.text.primary }} />
          <ChevronDown size={16} style={{ color: colors.text.secondary }} />
        </button>
      </div>
    </header>
  );
};

export default Header;
